import React, { useCallback, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { LatLngTuple } from 'leaflet'
import L from 'leaflet'
import { colors } from '../../../../../constants'
import { FlexContainer, YSpacing, DividerLine } from '@components/common'
import { MultiSelectDropDown } from '@components/common/form'
import { getMenuCoordinates } from '../menuSuggestionsHelpers'
import {
  Coordinate,
  GOScheduleMenu,
  Option,
  ShowInfoModalReq,
  GOClientLocation,
} from '@types'
import MenuDetails from '../MenuDetails'

const yellowChefIcon = new L.Icon({
  iconUrl: 'https://img.icons8.com/?size=100&id=20441&format=png&color=FFDE21',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
})

const redClientIcon = new L.Icon({
  iconUrl: 'https://img.icons8.com/?size=100&id=85794&format=png&color=FB1B1B',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
})

const purpleChefIcon = new L.Icon({
  iconUrl: 'https://img.icons8.com/?size=100&id=20441&format=png&color=9775FA',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
})

interface MenuGroupChef {
  id: string
  name: string
  chefPriorityType: string
}

interface ChefMenuGroup {
  chef: MenuGroupChef
  coords: Coordinate
  menus: GOScheduleMenu[]
}

interface MapMarker {
  id: string
  position: LatLngTuple
  icon: L.Icon
  popupContent: JSX.Element
}

/**
 * Custom hook that calculates chef menu groups and map markers
 * based on available menus and coordinates.
 *
 * Used to display chefs on a map and allow filtering/selecting them
 * in the group order schedule planner.
 */
interface UseChefsMapParams {
  hqId: number
  menus: GOScheduleMenu[]
  cuisineOptions: Option[]
  clientLocation?: GOClientLocation
  clientName?: string
}

interface UseChefsMapResult {
  chefMenuGroups: ChefMenuGroup[]
  filteredChefMenuGroups: ChefMenuGroup[]
  mapMarkers: MapMarker[]
  mapCenter: LatLngTuple | null
  menusCount: number
  searchTerm: string
  setSearchTerm: (term: string) => void
  includesCuisines: string[]
  setIncludesCuisines: (cuisines: string[]) => void
  excludesCuisines: string[]
  setExcludesCuisines: (cuisines: string[]) => void
  includeAnchorChefs: boolean | null
  setIncludeAnchorChefs: (includeAnchorChefs: boolean | null) => void
  selectedChefId: string | null
  handleSelectChef: (chefId: string, coords: Coordinate) => void
  cuisineOptions: Option[]
}

export const useChefsMap = ({
  hqId,
  menus,
  clientLocation,
  clientName,
  cuisineOptions,
}: UseChefsMapParams): UseChefsMapResult => {
  // State for the search and filter inputs
  const [allChefsSearch, setAllChefsSearch] = useState<string>('')
  const [includesCuisines, setIncludesCuisines] = useState<string[]>([])
  const [excludesCuisines, setExcludesCuisines] = useState<string[]>([])
  const [includeAnchorChefs, setIncludeAnchorChefs] = useState<boolean | null>(
    null,
  )

  // State for map interaction
  const [selectedChefId, setSelectedChefId] = useState<string | null>(null)
  const [mapCenter, setMapCenter] = useState<LatLngTuple | null>(
    clientLocation
      ? [
          clientLocation.coordinates.latitude,
          clientLocation.coordinates.longitude,
        ]
      : null,
  )

  // Calculate chef menu groups
  const chefMenuGroups = useMemo(() => {
    const chefMap = new Map<string, ChefMenuGroup>()

    menus.forEach((menu) => {
      if (menu.chef && menu.chef.id) {
        const coords = getMenuCoordinates(menu, hqId)

        if (chefMap.has(menu.chef.id)) {
          chefMap.get(menu.chef.id)?.menus.push(menu)
        } else {
          chefMap.set(menu.chef.id, {
            chef: { ...menu.chef, chefPriorityType: menu.chefPriorityType },
            coords,
            menus: [menu],
          })
        }
      }
    })

    return Array.from(chefMap.values())
  }, [menus, hqId])

  // Filter chef menu groups based on search term and cuisine filters
  const filteredChefMenuGroups = useMemo(() => {
    const searchTermLower = allChefsSearch.trim().toLowerCase()

    return (
      chefMenuGroups
        .map((chefGroup) => {
          // First filter the chef's menus
          const filteredMenus = chefGroup.menus.filter((menu) => {
            // Search term filter
            const matchesSearch =
              !searchTermLower ||
              chefGroup.chef.name.toLowerCase().includes(searchTermLower) ||
              menu.name.toLowerCase().includes(searchTermLower)

            if (!matchesSearch) {
              return false
            }

            // Includes cuisine filter
            const menuCuisines = menu.cuisineTags || []
            const matchesIncludesCuisines =
              includesCuisines.length === 0 ||
              includesCuisines.some((c) => menuCuisines.includes(c))

            if (!matchesIncludesCuisines) {
              return false
            }

            // Excludes cuisine filter
            const matchesExcludesCuisines =
              excludesCuisines.length === 0 ||
              !excludesCuisines.some((c) => menuCuisines.includes(c))

            if (!matchesExcludesCuisines) {
              return false
            }

            const isAnchorChef = chefGroup.chef.chefPriorityType === 'Anchor'
            const matchesAnchorChef =
              includeAnchorChefs === null || includeAnchorChefs === isAnchorChef

            return matchesAnchorChef
          })

          // Create a new chef group with the filtered menus
          return {
            ...chefGroup,
            menus: filteredMenus,
          }
        })
        // Then filter out chefs who have no menus left after filtering
        .filter((chefGroup) => chefGroup.menus.length > 0)
        // Sort by name
        .sort((a, b) => a.chef.name.localeCompare(b.chef.name))
    )
  }, [
    chefMenuGroups,
    allChefsSearch,
    includesCuisines,
    excludesCuisines,
    includeAnchorChefs,
  ])

  // Count total menus after filtering
  const menusCount = useMemo(() => {
    return filteredChefMenuGroups.reduce(
      (total, group) => total + group.menus.length,
      0,
    )
  }, [filteredChefMenuGroups])

  // Method to handle chef selection on the map or list
  const handleSelectChef = useCallback(
    (chefId: string, coords: Coordinate) => {
      if (selectedChefId === chefId) {
        // Deselect if already selected
        setSelectedChefId(null)
        if (clientLocation) {
          setMapCenter([
            clientLocation.coordinates.latitude,
            clientLocation.coordinates.longitude,
          ])
        }
      } else {
        setSelectedChefId(chefId)
        setMapCenter([coords.latitude, coords.longitude])
      }
    },
    [selectedChefId, clientLocation],
  )

  // Create markers for all chefs
  const getAllChefMarkers = useCallback(
    (chefGroups: ChefMenuGroup[], selectedId: string | null): MapMarker[] => {
      const markers: MapMarker[] = []

      // First add all non-selected chefs
      chefGroups
        .filter((chefGroup) => chefGroup.chef.id !== selectedId)
        .forEach((chefGroup) => {
          markers.push({
            id: `chef-${chefGroup.chef.id}`,
            position: [chefGroup.coords.latitude, chefGroup.coords.longitude],
            icon: purpleChefIcon,
            popupContent: (
              <React.Fragment>
                <strong>{chefGroup.chef.name}</strong>
                <br />
                <strong>Menus:</strong>
                <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                  {chefGroup.menus.map((menu, menuIndex) => (
                    <li key={menuIndex}>
                      {menu.name}
                      {menu.cuisineTags && menu.cuisineTags.length > 0 && (
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          {menu.cuisineTags.join(', ')}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </React.Fragment>
            ),
          })
        })

      // Then add selected chef (if any) so it appears on top
      const selectedChef =
        selectedId &&
        chefGroups.find((chefGroup) => chefGroup.chef.id === selectedId)
      if (selectedChef) {
        markers.push({
          id: `chef-${selectedChef.chef.id}-selected`,
          position: [
            selectedChef.coords.latitude,
            selectedChef.coords.longitude,
          ],
          icon: yellowChefIcon,
          popupContent: (
            <React.Fragment>
              <strong>{selectedChef.chef.name}</strong>
              <br />
              <strong>Menus:</strong>
              <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                {selectedChef.menus.map((menu, menuIndex) => (
                  <li key={menuIndex}>
                    {menu.name}
                    {menu.cuisineTags && menu.cuisineTags.length > 0 && (
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        {menu.cuisineTags.join(', ')}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </React.Fragment>
          ),
        })
      }

      return markers
    },
    [],
  )

  // Get all map markers including the client marker
  const mapMarkers = useMemo(() => {
    const markers: MapMarker[] = []

    // Always add client marker
    if (clientLocation) {
      markers.push({
        id: 'client',
        position: [
          clientLocation.coordinates.latitude,
          clientLocation.coordinates.longitude,
        ],
        icon: redClientIcon,
        popupContent: (
          <React.Fragment>
            <strong>{clientName}</strong>
            <br />
            Client Location
          </React.Fragment>
        ),
      })
    }

    // Add all chef markers
    markers.push(...getAllChefMarkers(filteredChefMenuGroups, selectedChefId))

    return markers
  }, [
    clientLocation,
    clientName,
    filteredChefMenuGroups,
    selectedChefId,
    getAllChefMarkers,
  ])

  return {
    chefMenuGroups,
    filteredChefMenuGroups,
    mapMarkers,
    mapCenter,
    menusCount,
    searchTerm: allChefsSearch,
    setSearchTerm: setAllChefsSearch,
    includesCuisines,
    setIncludesCuisines,
    excludesCuisines,
    setExcludesCuisines,
    includeAnchorChefs,
    setIncludeAnchorChefs,
    selectedChefId,
    handleSelectChef,
    cuisineOptions,
  }
}

// Interface for the AllChefsList component props
interface AllChefsListProps {
  chefMenuGroups: Array<ChefMenuGroup>
  menusCount: number
  searchTerm: string
  setSearchTerm: (term: string) => void
  includesCuisines: string[]
  setIncludesCuisines: (cuisines: string[]) => void
  excludesCuisines: string[]
  setExcludesCuisines: (cuisines: string[]) => void
  cuisineOptions: Option[]
  selectedChefId: string | null
  includeAnchorChefs: boolean | null
  setIncludeAnchorChefs: (includeAnchorChefs: boolean | null) => void
  onSelectChef: (chefId: string, coords: Coordinate) => void
  showInfoModal: (req: ShowInfoModalReq) => void
}

// The AllChefsList component
export const AllChefsList: React.FC<AllChefsListProps> = ({
  chefMenuGroups,
  menusCount,
  searchTerm,
  setSearchTerm,
  includesCuisines,
  setIncludesCuisines,
  excludesCuisines,
  setExcludesCuisines,
  cuisineOptions,
  includeAnchorChefs,
  setIncludeAnchorChefs,
  selectedChefId,
  onSelectChef,
  showInfoModal,
}) => {
  const [expandedChefs, setExpandedChefs] = useState<Record<string, boolean>>(
    {},
  )

  const toggleExpanded = (chefId: string) => {
    setExpandedChefs((prev) => ({
      ...prev,
      [chefId]: !prev[chefId],
    }))
  }

  return (
    <React.Fragment>
      <h3>All Available Menus by Chef</h3>
      <YSpacing height="10px" />
      <p>
        Showing {chefMenuGroups.length} chefs with {menusCount} total menus
      </p>

      {menusCount === 0 ? (
        <NoResultsMessage>
          Please find available menus first before mapping menus
        </NoResultsMessage>
      ) : (
        <>
          <FiltersSection>
            <FiltersSectionTitle>Filters</FiltersSectionTitle>

            <InputLabel htmlFor="chefSearch">
              Search by Chef or Menu Name:
            </InputLabel>
            <SearchInput
              id="chefSearch"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type to search..."
            />
            <YSpacing height="15px" />

            <InputLabel htmlFor="includeAnchorChefs">Chef Type</InputLabel>
            <Select
              id="includeAnchorChefs"
              value={
                includeAnchorChefs == null
                  ? 'all'
                  : includeAnchorChefs
                    ? 'anchor'
                    : 'notAnchor'
              }
              onChange={(e) => {
                if (e.target.value === 'all') {
                  setIncludeAnchorChefs(null)
                } else {
                  setIncludeAnchorChefs(e.target.value === 'anchor')
                }
              }}
            >
              <option value="all">All</option>
              <option value="anchor">Anchor</option>
              <option value="notAnchor">Not Anchor</option>
            </Select>
            <YSpacing height="15px" />

            <InputLabel htmlFor="includesCuisines">
              Must Include at Least One of These Cuisines:
            </InputLabel>
            <MultiSelectDropDown
              label=""
              includeSelectAll={true}
              options={cuisineOptions}
              checkedOptions={includesCuisines}
              searchPlaceholder="Select cuisines..."
              onChange={(values) => setIncludesCuisines(values)}
              width="100%"
              renderCheckedOptions={true}
            />
            <YSpacing height="15px" />

            <InputLabel htmlFor="excludesCuisines">
              Must Not Include Any of These Cuisines:
            </InputLabel>
            <MultiSelectDropDown
              label=""
              includeSelectAll={true}
              options={cuisineOptions}
              checkedOptions={excludesCuisines}
              searchPlaceholder="Select cuisines..."
              onChange={(values) => setExcludesCuisines(values)}
              width="100%"
              renderCheckedOptions={true}
            />
          </FiltersSection>

          <ResultsDivider />

          <ResultsSection>
            <ResultsSectionTitle>Results</ResultsSectionTitle>

            <FlexContainer flexDirection="column" width="100%">
              {chefMenuGroups.length > 0 ? (
                chefMenuGroups.map((chefGroup, index) => (
                  <ChefCard
                    key={index}
                    selected={selectedChefId === chefGroup.chef.id}
                    onClick={() =>
                      onSelectChef(chefGroup.chef.id, chefGroup.coords)
                    }
                  >
                    <ChefTitle>{chefGroup.chef.name}</ChefTitle>
                    <SuggestionDetail>
                      <strong>Menus Offered:</strong> {chefGroup.menus.length}
                    </SuggestionDetail>
                    {chefGroup.menus.length > 0 ? (
                      <MenuListContainer>
                        {(expandedChefs[chefGroup.chef.id]
                          ? chefGroup.menus
                          : chefGroup.menus.slice(0, 3)
                        ).map((menu, menuIndex) => (
                          <React.Fragment key={menuIndex}>
                            <MenuItemContainer>
                              <MenuItemDetails>
                                <MenuItemName>{menu.name}</MenuItemName>
                                <MenuItemCuisines>
                                  {menu.cuisineTags &&
                                    menu.cuisineTags.map(
                                      (cuisine, cuisineIndex) => (
                                        <CuisineTag key={cuisineIndex}>
                                          {cuisine}
                                        </CuisineTag>
                                      ),
                                    )}
                                </MenuItemCuisines>
                              </MenuItemDetails>
                              <ViewDetailsButton
                                onClick={(e) => {
                                  e.stopPropagation() // Prevent triggering the parent onClick
                                  showInfoModal({
                                    title: `${menu.name} Details`,
                                    content: <MenuDetails menu={menu} />,
                                  })
                                }}
                              >
                                View Details
                              </ViewDetailsButton>
                            </MenuItemContainer>
                            {menuIndex < chefGroup.menus.length - 1 && (
                              <MenuSeparator />
                            )}
                          </React.Fragment>
                        ))}
                      </MenuListContainer>
                    ) : (
                      <NoMenusMessage>
                        No menus match your cuisine filters
                      </NoMenusMessage>
                    )}
                    {chefGroup.menus.length > 3 && (
                      <MoreMenusIndicator>
                        {expandedChefs[chefGroup.chef.id] ? (
                          <div
                            className="flex flex-row items-center underline text-blue-500"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleExpanded(chefGroup.chef.id)
                            }}
                          >
                            hide menus
                          </div>
                        ) : (
                          <div
                            className="flex flex-row items-center underline text-blue-500"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleExpanded(chefGroup.chef.id)
                            }}
                          >
                            + {chefGroup.menus.length - 3} more menus
                          </div>
                        )}
                      </MoreMenusIndicator>
                    )}
                  </ChefCard>
                ))
              ) : (
                <NoResultsMessage>
                  No chefs or menus match your search criteria
                </NoResultsMessage>
              )}
            </FlexContainer>
          </ResultsSection>
        </>
      )}
    </React.Fragment>
  )
}

// Styled components
const BaseCard = styled.div`
  padding: 15px;
  margin-bottom: 12px;
  background-color: white;
  border: 1px solid ${colors.gray200};
  border-radius: 6px;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
`

const ChefCard = styled(BaseCard)<{ selected: boolean }>`
  cursor: pointer;
  background-color: ${(props) => (props.selected ? colors.gray200 : 'white')};
  border: 1px solid ${colors.gray200};

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    background-color: ${(props) =>
      props.selected ? colors.gray200 : colors.gray100};
  }
`

const ChefTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 1.05em;
  font-weight: 600;
  color: ${colors.blue400};
`

const SuggestionDetail = styled.p`
  margin: 4px 0;
  font-size: 0.9em;
  color: ${colors.gray400};
  line-height: 1.3;

  strong {
    color: ${colors.dark500};
    font-weight: 500;
  }
`

const MenuListContainer = styled.div`
  width: 100%;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${colors.gray200};
`

const MenuItemContainer = styled.div`
  margin: 10px 0;
  padding: 8px;
  background-color: ${colors.gray100};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const MenuItemDetails = styled.div`
  flex: 1;
`

const MenuItemName = styled.div`
  font-size: 0.95em;
  font-weight: 500;
  color: ${colors.dark500};
  margin-bottom: 3px;
`

const MenuItemCuisines = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const MenuSeparator = styled.div`
  height: 1px;
  background-color: ${colors.gray100}; // Lighter separator
  margin: 10px 0; // Space around separator
`

const InputLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.9em;
  font-weight: bold;
  color: ${colors.dark500};
`

const SearchInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${colors.gray200};
  border-radius: 6px;
  font-size: 0.95em;
  box-sizing: border-box;

  &:focus {
    border-color: ${colors.blue400};
    outline: none;
    box-shadow: 0 0 0 2px ${colors.blue100};
  }
`

const NoResultsMessage = styled.p`
  text-align: center;
  color: ${colors.gray400};
  font-style: italic;
`

const NoMenusMessage = styled.p`
  text-align: center;
  color: ${colors.gray400};
  font-style: italic;
  margin-top: 10px;
  font-size: 0.9em;
`

const FiltersSection = styled.div`
  background-color: ${colors.gray100};
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${colors.gray200};
`

const FiltersSectionTitle = styled.h4`
  color: ${colors.blue400};
  margin: 0 0 15px 0;
  font-size: 1.05em;
  font-weight: 600;
  border-bottom: 1px solid ${colors.gray200};
  padding-bottom: 8px;
`

const ResultsDivider = styled(DividerLine)`
  margin: 0 0 20px 0;
`

const ResultsSection = styled.div`
  margin-top: 15px;
`

const ResultsSectionTitle = styled.h4`
  color: ${colors.blue400};
  margin: 0 0 15px 0;
  font-size: 1.05em;
  font-weight: 600;
`

const ViewDetailsButton = styled.button`
  background-color: ${colors.blue300};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  margin-left: 10px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.blue400};
  }
`

const CuisineTag = styled.span`
  background-color: ${colors.blue100};
  border: 1px solid ${colors.blue200};
  color: ${colors.blue400};
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 0.7em;
`

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid ${colors.gray200};
  border-radius: 6px;
  background-color: white;
  font-size: 0.95em;
  box-sizing: border-box;
  appearance: none;
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E');
  background-repeat: no-repeat;
  background-position: right 10px top 50%;
  background-size: 0.65em auto;
  padding-right: 30px;

  &:focus {
    border-color: ${colors.blue400};
    outline: none;
    box-shadow: 0 0 0 2px ${colors.blue100};
  }
`

const MoreMenusIndicator = styled.div`
  text-align: center;
  font-size: 0.85em;
  color: ${colors.gray400};
  font-style: italic;
  padding: 5px 0;
`
